import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

import type {
  ResultType,
  DiscoveryParametersDeserialized,
  DiscoveryParametersSerialized,
  UnifiedDiscoveryResultItem,
} from '../../utils/discovery/types';
import { serializeParams } from '../../utils/discovery/utils';

const queryKey = (
  params: Pick<
    DiscoveryParametersDeserialized,
    'page' | 'sortBy' | 'sortDirection' | 'timeframe' | 'filters' | 'preset' | 'searchQuery'
  >,
) => [
  'discovery-table',
  params.page,
  params.sortBy,
  params.sortDirection,
  params.timeframe,
  params.filters,
  params.preset,
  params.searchQuery ?? '',
];

function makeQueryFn(params: DiscoveryParametersDeserialized) {
  const { page, sortBy, sortDirection, timeframe, filters, preset, searchQuery } = params;
  return async ({ signal }: { signal: AbortSignal }) => {
    const url = new URL('/api/discovery/tokens', window.location.origin);
    const paramsObject: DiscoveryParametersSerialized = serializeParams({
      page,
      sortBy,
      sortDirection,
      filters,
      timeframe,
      preset,
      pageDiscovery: false,
      searchQuery,
    });
    const params = new URLSearchParams();
    Object.entries(paramsObject).forEach(([key, value]) => {
      if (Array.isArray(value)) {
        params.set(key, value.join(','));
      } else {
        if (typeof value === 'number') {
          params.set(key, value.toString());
        } else if (typeof value === 'string') {
          params.set(key, value);
        }
      }
    });
    url.search = params.toString();
    const result = (await fetch(url.toString(), { signal }).then((res) =>
      res.json(),
    )) as ResultType;
    const out = {
      result: [] as UnifiedDiscoveryResultItem[],
      didError: false,
      totalCount: undefined as number | undefined,
    };
    if (result.type === 'success') {
      out.result = result.data ?? [];
      out.totalCount = result.totalCount;
    }
    if (result.type === 'error') {
      out.didError = true;
      if (result.fallbackResult) {
        out.result = result.fallbackResult;
      }
    }
    return out;
  };
}

export function useDiscoveryTable(
  params: DiscoveryParametersDeserialized & {
    query?: Omit<
      Parameters<
        typeof useQuery<{
          result: UnifiedDiscoveryResultItem[];
          didError: boolean;
          totalCount?: number;
        }>
      >[0],
      'queryKey' | 'queryFn'
    >;
  },
) {
  return useQuery<{
    result: UnifiedDiscoveryResultItem[];
    didError: boolean;
    totalCount?: number;
  }>({
    queryKey: queryKey(params),
    queryFn: makeQueryFn(params),
    ...(params.query ?? {}),
  });
}

export function useDiscoveryTablePrefetch() {
  const queryClient = useQueryClient();
  const prefetchFun = useCallback(
    (params: DiscoveryParametersDeserialized) => {
      queryClient.prefetchQuery({
        queryKey: queryKey(params),
        queryFn: makeQueryFn(params),
      });
    },
    [queryClient],
  );

  return prefetchFun;
}
